import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { TEAM_ROLES } from '../../../data/nurseAppModels';

const TeamSelector = () => {
  const { teams, currentTeam, setTeam } = useTeam();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Save selected team to localStorage
  const handleTeamSelect = (team) => {
    setTeam(team);
    setShowDropdown(false);
    // Save to localStorage
    localStorage.setItem('nurseapp_currentTeamId', team.id);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case TEAM_ROLES.LEADER:
        return 'หัวหน้าทีม';
      case TEAM_ROLES.DEPUTY_LEADER:
        return 'รองหัวหน้า';
      default:
        return 'สมาชิก';
    }
  };

  if (!currentUser || !teams || teams.length === 0) {
    return null;
  }

  // If only one team, show simplified version
  if (teams.length === 1) {
    return (
      <div className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-white/20 rounded-lg backdrop-blur-sm max-w-[140px] sm:max-w-none">
        <span className="text-base sm:text-lg flex-shrink-0">👥</span>
        <div className="text-left min-w-0 flex-1">
          <p className="text-xs text-white/80 hidden sm:block">ทีมปัจจุบัน</p>
          <p className="text-xs sm:text-sm font-semibold text-white truncate">
            {currentTeam?.name || teams[0].name}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Team Selector Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-white/20 rounded-lg backdrop-blur-sm hover:bg-white/30 transition-all duration-200 max-w-[140px] sm:max-w-none"
      >
        <span className="text-base sm:text-lg flex-shrink-0">👥</span>
        <div className="text-left min-w-0 flex-1 hidden sm:block">
          <p className="text-xs text-white/80">ทีมปัจจุบัน</p>
          <p className="text-sm font-semibold text-white truncate max-w-[120px]">
            {currentTeam?.name || 'เลือกทีม'}
          </p>
        </div>
        {/* Mobile: Show only icon and team name (no label) */}
        <div className="text-left min-w-0 flex-1 sm:hidden">
          <p className="text-xs font-semibold text-white truncate">
            {currentTeam?.name || 'เลือกทีม'}
          </p>
        </div>
        <svg
          className={`w-3 h-3 sm:w-4 sm:h-4 text-white transition-transform duration-200 flex-shrink-0 ${showDropdown ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[80vh] overflow-y-auto">
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">เลือกทีมที่ต้องการใช้งาน</h3>
            <p className="text-xs text-gray-500 mt-1">ข้อมูลตารางเวรและอุปกรณ์จะเปลี่ยนตามทีมที่เลือก</p>
          </div>

          <div className="p-2">
            {teams.filter((team, index, self) => 
              // Remove duplicates: keep only first occurrence of each team id
              index === self.findIndex(t => t.id === team.id)
            ).map((team) => {
              const isActive = currentTeam?.id === team.id;
              return (
                <button
                  key={team.id}
                  onClick={() => handleTeamSelect(team)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'hover:bg-gray-50 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-lg">👥</span>
                        <h4 className="font-semibold text-gray-800 truncate">{team.name}</h4>
                        {isActive && (
                          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full whitespace-nowrap">
                            ทีมปัจจุบัน
                          </span>
                        )}
                      </div>
                      {team.description && (
                        <p className="text-xs text-gray-600 truncate mb-1">{team.description}</p>
                      )}
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <span>สมาชิก: {team.members?.length || 0} คน</span>
                        <span>•</span>
                        <span>{getRoleLabel(team.userRole)}</span>
                      </div>
                    </div>
                    {isActive && (
                      <svg
                        className="w-5 h-5 text-blue-500 flex-shrink-0 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-2 border-t border-gray-200">
            <button
              onClick={() => {
                setShowDropdown(false);
                navigate('/nurse/teams');
              }}
              className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>สร้างทีมใหม่</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSelector;

